import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import { getAccessToken, hadSession, setSessionExpiredHandler } from '../api/client';
import type { CurrentUserDto, LoginDto, LoginResultDto } from '../api/types';

/* ------------------------------------------------------------------ *
 *  Panel oturumu.
 *
 *  Yetkiler token'daki `permission` taleplerinden turer ve `/api/auth/me`
 *  ile gelir. Arayuz menuleri ve dugmeleri bunlara gore cizilir; asil
 *  denetim her zaman sunucudadir.
 * ------------------------------------------------------------------ */

const BACK_OFFICE_ROLES = [
  'SuperAdmin',
  'Admin',
  'CatalogManager',
  'OrderManager',
  'InventoryManager',
  'ContentManager',
  'SupportAgent',
];

interface AuthState {
  user: CurrentUserDto | null;
  booting: boolean;
  isAuthenticated: boolean;
  /** Panele girebilecek bir role sahip mi. */
  isStaff: boolean;
  can: (permission: string) => boolean;
  canAny: (...permissions: string[]) => boolean;
  hasRole: (role: string) => boolean;
  login: (dto: LoginDto) => Promise<LoginResultDto>;
  completeTwoFactor: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth yalnızca AuthProvider içinde kullanılabilir.');
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUserDto | null>(null);
  const [booting, setBooting] = useState(true);
  const queryClient = useQueryClient();

  const loadUser = useCallback(async () => {
    try {
      setUser(await authApi.me());
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Daha once giris yapilmadiysa yenileme denemesi bosunadir.
        if (!getAccessToken() && hadSession()) {
          await authApi.refresh();
        }
        if (getAccessToken() && !cancelled) {
          await loadUser();
        }
      } catch {
        /* Oturum yok; giris ekrani gosterilir. */
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadUser]);

  useEffect(() => {
    setSessionExpiredHandler(() => {
      setUser(null);
      queryClient.clear();
    });
    return () => setSessionExpiredHandler(null);
  }, [queryClient]);

  const login = useCallback(
    async (dto: LoginDto) => {
      const result = await authApi.login(dto);
      if (!result.requiresTwoFactor) {
        await loadUser();
      }
      return result;
    },
    [loadUser],
  );

  const completeTwoFactor = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthState>(() => {
    const permissions = new Set(user?.permissions ?? []);
    const roles = new Set(user?.roles ?? []);

    return {
      user,
      booting,
      isAuthenticated: user !== null,
      isStaff: BACK_OFFICE_ROLES.some((role) => roles.has(role)),
      // SuperAdmin tum izinlere sahiptir; ayri ayri kontrol edilmez.
      can: (permission) => roles.has('SuperAdmin') || permissions.has(permission),
      canAny: (...list) =>
        roles.has('SuperAdmin') || list.some((permission) => permissions.has(permission)),
      hasRole: (role) => roles.has(role),
      login,
      completeTwoFactor,
      logout,
      refresh: loadUser,
    };
  }, [user, booting, login, completeTwoFactor, logout, loadUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
